/**
 * Respect / card HTTP paths relative to `API_BASE_URL` (e.g. `/api`).
 * Use with `API_BASE_URL` from `config/env`, or RTK `baseApi` (which already prefixes `/api`).
 */

const enc = encodeURIComponent;

/** Player / shared card surface (`/api/cards/...` and related respect reads). */
export const respectCardsPlayerPaths = {
  lockedSummary: "/cards/locked-summary",
  agentInfluenceRoles: "/cards/agent-influence-roles",
  dropToday: "/cards/drop/today",
  dropPull: "/cards/drop/pull",
  /** cards-drop-lobby — join today's gathering lobby. */
  dropLobbyJoin: "/cards/drop/lobby/join",
  /** cards-drop-lobby — viewer-aware snapshot of today's lobby (204 when none). */
  dropLobbyToday: "/cards/drop/lobby/today",
  /** cards-drop-lobby — SSE stream of lobby events. */
  dropLobbyStream: "/cards/drop/lobby/stream",
  /** cards-drop-lobby — public newest-first feed of recent pulls. */
  dropActivityFeed: "/cards/drop/activity-feed",
  myCards: "/cards/my-cards",
  myEffects: "/cards/my-effects",
  /** Cheap hand badge summary (60s poll + focus refetch). */
  handSummary: "/cards/hand-summary",
  /** Primary handler under `/api/v2/respect`. */
  publicActiveEffects: "/v2/respect/active-effects",
  /** Backend alias — same behavior as `publicActiveEffects`. */
  publicActiveEffectsCardsNamespace: "/v2/respect/cards/active-effects",
  contextFeed: (surface: string) => `/cards/context-feed/${enc(surface)}`,
  /** Player-facing recipient search for gift/target cards (e.g. Tribute). */
  recipientSearch: "/cards/users/search",
  play: (cardInstanceId: string) => `/cards/play/${enc(cardInstanceId)}`,
  cardArt: (slug: string) => `/cards/art/${enc(slug)}`,
} as const;

/** Admin card tooling (`/api/v2/respect/admin/...`). */
export const respectCardsAdminPaths = {
  cards: "/v2/respect/admin/cards",
  card: (slug: string) => `/v2/respect/admin/cards/${enc(slug)}`,
  cardToggle: (slug: string) => `/v2/respect/admin/cards/${enc(slug)}/toggle`,
  cardGeneratePrompt: (slug: string) =>
    `/v2/respect/admin/cards/${enc(slug)}/generate-prompt`,
  cardGenerateImages: (slug: string) =>
    `/v2/respect/admin/cards/${enc(slug)}/generate-images`,
  cardSelectImage: (slug: string) =>
    `/v2/respect/admin/cards/${enc(slug)}/select-image`,
  recommend: "/v2/respect/admin/cards/recommend",
  catalog: "/v2/respect/admin/cards/catalog",
  schedule: "/v2/respect/admin/cards/schedule",
  scheduleDay: (date: string) =>
    `/v2/respect/admin/cards/schedule/${enc(date)}`,
  scheduleRegenerate: (date: string) =>
    `/v2/respect/admin/cards/schedule/${enc(date)}/regenerate`,
  schedulePreview: (date: string) =>
    `/v2/respect/admin/cards/schedule/${enc(date)}/preview`,
  scheduleHorizon: "/v2/respect/admin/cards/schedule/horizon",
  /** cards-drop-lobby phase 5 — open today's drop immediately. */
  dropLaunch: "/v2/respect/admin/cards/drop/launch",
  /** cards-drop-lobby phase 5 — bar a user from today's pull ({ username, reason }). */
  dropBlock: "/v2/respect/admin/cards/drop/block",
  /** cards-drop-lobby phase 5 — lift a user's block for today ({ username }). */
  dropUnblock: "/v2/respect/admin/cards/drop/unblock",
  /** cards-drop v2 — wipe a day's lobby + drop so it can re-run cleanly. */
  dropReset: "/v2/respect/admin/cards/drop/reset",
  impactMap: "/v2/respect/admin/cards/impact-map",
  impactMapCard: (slug: string) =>
    `/v2/respect/admin/cards/impact-map/${enc(slug)}`,
  impactMapCoverage: "/v2/respect/admin/cards/impact-map/coverage-report",
  runtimeEvents: "/v2/respect/admin/runtime/events",
  runtimeEffects: "/v2/respect/admin/runtime/effects",
  runtimeActions: "/v2/respect/admin/runtime/actions",
  runtimeAotdToday: "/v2/respect/admin/runtime/aotd/today",
  engineChat: "/v2/respect/admin/engine/chat",
  usersSearch: "/v2/respect/admin/users/search",
  userCards: (userId: string) =>
    `/v2/respect/admin/users/${enc(userId)}/cards`,
  cardInstance: (instanceId: string) =>
    `/v2/respect/admin/card-instances/${enc(instanceId)}`,
  /** Dev-only — backend rejects in production. */
  devSimulatePull: "/v2/respect/admin/dev/simulate-pull",
  devSimulateCatalog: "/v2/respect/admin/dev/simulate-catalog",
  /** cards-drop-lobby phase 11/12 — dev sim-lobby harness (each 404s in prod). */
  devSimLobbyCreate: "/v2/respect/admin/dev/sim-lobby/create",
  devSimLobby: (id: string) => `/v2/respect/admin/dev/sim-lobby/${enc(id)}`,
  devSimLobbyStep: (id: string) => `/v2/respect/admin/dev/sim-lobby/${enc(id)}/step`,
  devSimLobbyReset: (id: string) => `/v2/respect/admin/dev/sim-lobby/${enc(id)}/reset`,
} as const;
